import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import ArchiveIcon from '@mui/icons-material/Archive'
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material'
import Button from '@mui/material/Button'
import CatalogAPI from 'api/faber/CatalogAPI'
import NormalText from 'common/Typography/NormalText'
import OrbitSession from 'hooks/useCookies'

interface ManagePageFormProps {
  pageId: string
  destinationName: string
}

interface CatalogApiRow {
  id?: string
  catalogId?: string
  catalog_id?: string
  name?: string
  dataInGB?: number
  validityInDays?: number
  voiceInMins?: number
  visibility?: boolean
  isVisible?: boolean
}

interface CatalogInfoResponse {
  data?: CatalogApiRow[]
  body?: {
    data?: CatalogApiRow[]
  }
}

interface CatalogTableRow {
  id: string
  catalogId: string
  name: string
  dataInGB: number
  validityInDays: number
  voiceInMins: number
  visibility: boolean
}

export const ManagePageForm = ({ pageId, destinationName }: ManagePageFormProps) => {
  const faberToken = OrbitSession.get('faberToken')

  const [rows, setRows] = useState<CatalogTableRow[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)
  const [selectedCatalog, setSelectedCatalog] = useState<CatalogTableRow | null>(null)
  const [isArchiving, setIsArchiving] = useState(false)

  const shouldFetch = Boolean(pageId && faberToken)

  const normalizeRows = useMemo(
    () => (payload: CatalogInfoResponse | undefined) => {
      const rawRows = payload?.data || payload?.body?.data || []
      if (!Array.isArray(rawRows)) {
        return [] as CatalogTableRow[]
      }

      return rawRows
        .map((row: CatalogApiRow) => {
          const catalogId = row.catalogId || row.catalog_id
          const id = row.id || catalogId
          if (!id || !catalogId) {
            return null
          }

          return {
            id: String(id),
            catalogId: String(catalogId),
            name: row.name || '-',
            dataInGB: Number(row.dataInGB || 0),
            validityInDays: Number(row.validityInDays || 0),
            voiceInMins: Number(row.voiceInMins || 0),
            visibility: Boolean(
              typeof row.visibility === 'boolean'
                ? row.visibility
                : row.isVisible
            )
          } as CatalogTableRow
        })
        .filter((row): row is CatalogTableRow => Boolean(row))
    },
    []
  )

  const fetchCatalogInfo = useCallback(async () => {
    if (!shouldFetch) {
      setRows([])
      return
    }

    setIsLoading(true)
    try {
      const response = (await CatalogAPI.getCatalogInfoByPageId(
        faberToken,
        pageId
      )) as CatalogInfoResponse
      setRows(normalizeRows(response))
    } catch (error) {
      toast.error('Failed to fetch catalog items')
      setRows([])
    } finally {
      setIsLoading(false)
    }
  }, [faberToken, normalizeRows, pageId, shouldFetch])

  useEffect(() => {
    void fetchCatalogInfo()
  }, [fetchCatalogInfo])

  const handleOpenConfirmDialog = (row: CatalogTableRow) => {
    setSelectedCatalog(row)
    setOpenConfirmDialog(true)
  }

  const handleCloseConfirmDialog = () => {
    if (isArchiving) {
      return
    }
    setOpenConfirmDialog(false)
    setSelectedCatalog(null)
  }

  const handleConfirmArchive = async () => {
    if (!selectedCatalog?.catalogId) {
      toast.error('Invalid catalog item selected.')
      return
    }

    setIsArchiving(true)
    try {
      await CatalogAPI.archiveCatalogFormPage(faberToken, {
        destinationName,
        catalogId: selectedCatalog.catalogId,
        page_id: pageId
      })
      toast.success('Catalog item archived successfully!')
      handleCloseConfirmDialog()
      await fetchCatalogInfo()
    } catch (error: unknown) {
      toast.error(
        `Failed to archive catalog item: ${String(
          (error as Error)?.message || 'An error occurred'
        )}`
      )
    } finally {
      setIsArchiving(false)
    }
  }

  return (
    <Box>
      <NormalText isBold>Underlying Package</NormalText>

      <TableContainer component={Paper} sx={{ mt: 1, maxHeight: 620 }}>
        <Table stickyHeader size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Catalog ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell align='right'>Data In GB</TableCell>
              <TableCell align='right'>Validity In Days</TableCell>
              <TableCell align='right'>Voice In Mins</TableCell>
              <TableCell align='center'>Visibility</TableCell>
              <TableCell align='center'>Archive</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={7} align='center' sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}

            {!isLoading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align='center' sx={{ py: 4 }}>
                  No catalog items found for this page.
                </TableCell>
              </TableRow>
            )}

            {!isLoading &&
              rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.catalogId}</TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell align='right'>{row.dataInGB}</TableCell>
                  <TableCell align='right'>{row.validityInDays}</TableCell>
                  <TableCell align='right'>{row.voiceInMins}</TableCell>
                  <TableCell align='center'>
                    <Box
                      component='span'
                      sx={{
                        color: row.visibility ? 'success.main' : 'error.main',
                        fontWeight: 600
                      }}
                    >
                      {row.visibility ? 'Visible' : 'Hidden'}
                    </Box>
                  </TableCell>
                  <TableCell align='center'>
                    <IconButton
                      color='primary'
                      aria-label='archive-catalog-item'
                      onClick={() => handleOpenConfirmDialog(row)}
                      disabled={isArchiving}
                    >
                      <ArchiveIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={openConfirmDialog}
        onClose={handleCloseConfirmDialog}
        aria-labelledby='archive-confirm-title'
        aria-describedby='archive-confirm-description'
      >
        <DialogTitle id='archive-confirm-title'>Confirm Archive</DialogTitle>
        <DialogContent>
          <DialogContentText id='archive-confirm-description'>
            Are you sure you want to archive this catalog item? This action cannot
            be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} disabled={isArchiving}>
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={handleConfirmArchive}
            disabled={isArchiving || !selectedCatalog}
          >
            {isArchiving ? 'Archiving...' : 'Archive'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
